// File: src/pages/BlogPage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const INDUSTRIES = ['All', 'Marketing', 'IT - Phần mềm', 'Tài chính - Kế toán', 'Xây dựng', 'Logistics', 'Nhân sự (HR)', 'Y tế', 'Giáo dục'];

// Icons
const IconSearch = () => <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const IconEye = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const IconHeart = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;

export default function BlogPage() {
    const [blogs, setBlogs] = useState([]);
    const [selectedIndustry, setSelectedIndustry] = useState('All');
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(true);
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    const fetchBlogs = async () => {
        setLoading(true);
        try {
            // ✅ Cập nhật URL để hỗ trợ cả Industry và Search
            let url = `${apiBaseUrl}/api/blogs?`;
            if (selectedIndustry !== 'All') url += `industry=${encodeURIComponent(selectedIndustry)}&`;
            if (searchText) url += `search=${encodeURIComponent(searchText)}`;
            
            const res = await fetch(url);
            const data = await res.json();
            setBlogs(data);
        } catch (error) { console.error(error); } 
        finally { setLoading(false); }
    };

    // Gọi API khi đổi ngành hoặc Enter tìm kiếm
    useEffect(() => {
        fetchBlogs();
    }, [selectedIndustry]);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchBlogs();
    };

    return (
        <div className="bg-gray-50 min-h-screen pt-28 pb-12 font-sans">
            <div className="max-w-7xl mx-auto px-4">
                
                {/* 1. Header & Search */}
                <div className="text-center mb-12">
                    <span className="text-blue-600 font-bold tracking-wider uppercase text-sm">Blog & Kiến thức</span>
                    <h1 className="text-4xl font-extrabold text-gray-900 mt-2 mb-4">Phát triển sự nghiệp cùng SkillMatch</h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
                        Những bài viết chuyên sâu, cập nhật xu hướng thị trường và bí quyết thăng tiến dành riêng cho bạn.
                    </p>

                    {/* ✅ THANH TÌM KIẾM MỚI */}
                    <form onSubmit={handleSearch} className="max-w-lg mx-auto relative">
                        <input 
                            type="text" 
                            className="w-full pl-12 pr-4 py-4 rounded-full border border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                            placeholder="Tìm kiếm bài viết "
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                            <IconSearch />
                        </span>
                        <button type="submit" className="absolute right-2 top-2 bottom-2 bg-blue-600 text-white px-6 rounded-full font-bold hover:bg-blue-700 transition-all">
                            Tìm
                        </button>
                    </form>
                </div>

                {/* 2. Industry Filter */}
                <div className="flex overflow-x-auto gap-3 pb-4 mb-8 no-scrollbar justify-start md:justify-center">
                    {INDUSTRIES.map(ind => (
                        <button
                            key={ind}
                            onClick={() => { setSelectedIndustry(ind); setSearchText(''); }} // Reset search khi đổi ngành
                            className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-semibold transition-all
                                ${selectedIndustry === ind 
                                    ? 'bg-gray-900 text-white shadow-lg transform scale-105' 
                                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                                }`}
                        >
                            {ind}
                        </button>
                    ))}
                </div>

                {/* 3. Blog Grid */}
                {loading ? (
                        <div style={{ textAlign: 'center', padding: 60, color: '#6B7280' }}>
                            <span className="material-symbols-outlined spin">sync</span> Đang tải dữ liệu...
                        </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {blogs.map((blog) => (
                            <Link key={blog._id} to={`/blog/${blog.slug}`} className="group block h-full">
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col transform hover:-translate-y-1">
                                    {/* Image */}
                                    <div className="h-52 bg-gray-200 overflow-hidden relative">
                                        <img 
                                            src={blog.thumbnail || "https://via.placeholder.com/800x600"} 
                                            alt={blog.title} 
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                        />
                                        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-lg text-xs font-bold text-gray-900 shadow-sm">
                                            {blog.industry}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-6 flex-1 flex flex-col">
                                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                                            <span>{new Date(blog.publishedAt).toLocaleDateString('vi-VN')}</span>
                                            <span>•</span>
                                            <span className="font-medium text-gray-700">{blog.author.name}</span>
                                        </div>
                                        
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
                                            {blog.title}
                                        </h3>
                                        
                                        <p className="text-gray-500 text-sm line-clamp-3 mb-5 flex-1 leading-relaxed">
                                            {blog.summary}
                                        </p>

                                        {/* Footer Stats */}
                                        <div className="flex items-center justify-between border-t border-gray-50 pt-4 mt-auto text-gray-400 text-xs font-medium">
                                            <div className="flex gap-4">
                                                <span className="flex items-center gap-1"><IconEye /> {blog.views}</span>
                                                <span className="flex items-center gap-1"><IconHeart /> {blog.likes?.length || 0}</span>
                                                <span className="flex items-center gap-1">💬 {blog.comments?.length || 0}</span>
                                            </div>
                                            <span className="text-blue-600 font-bold group-hover:translate-x-1 transition-transform">Đọc ngay →</span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
                
                {!loading && blogs.length === 0 && (
                    <div className="text-center py-24">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-4xl">🔍</div>
                        <h3 className="text-xl font-bold text-gray-900">Không tìm thấy bài viết nào</h3>
                        <p className="text-gray-500 mt-2">Thử tìm kiếm với từ khóa khác hoặc chọn ngành nghề khác xem sao.</p>
                        <button onClick={() => { setSelectedIndustry('All'); setSearchText(''); }} className="mt-6 text-blue-600 font-bold hover:underline">
                            Xem tất cả bài viết
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}